/**
 * The printable invoice.
 *
 * Deliberately the same document the control plane produces: a client
 * who has been sent an invoice by us and then prints their own copy
 * should get the same piece of paper, not a second design.
 *
 * Printed rather than generated as a PDF because every browser can
 * save to PDF, it needs no library, and the result can also be handed
 * over on paper.
 */
type Line = {
  description: string;
  quantity: number;
  unit_cents: number;
  amount_cents: number;
};

const esc = (v: unknown) => String(v ?? '').replace(/[<>&"]/g,
  (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;',
            '"': '&quot;' }[c] ?? c));

const day = (iso: string | null) => {
  if (!iso) return '--';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}-${m[3]}-${m[1]}` : '--';
};

const money = (cents: number, ccy = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: ccy, maximumFractionDigits: 2,
  }).format((cents ?? 0) / 100);

const MARK = '<svg class="mark" viewBox="0 0 34 34" xmlns="http://'
  + 'www.w3.org/2000/svg"><rect width="34" height="34" rx="9"'
  + ' fill="#0F6E56"/><g fill="none" stroke="#fff" stroke-width="2.7"'
  + ' stroke-linecap="round" stroke-linejoin="round">'
  + '<path d="M9 17.6 L17 11.4 L25 17.6"/>'
  + '<path d="M12.1 22.3 L17 18.4 L21.9 22.3"/></g>'
  + '<circle cx="17" cy="26.6" r="1.75" fill="#fff"/></svg>';

const STYLE = `
 @page { margin: 16mm }
 * { -webkit-print-color-adjust: exact; print-color-adjust: exact }
 body { font: 13px/1.5 -apple-system,system-ui,"Segoe UI",sans-serif;
        color:#1e2b26; margin:0; background:#eceff1 }
 .sheet { max-width:820px; margin:28px auto; background:#fff;
          padding:48px; box-shadow:0 2px 18px rgba(20,40,34,.12) }
 @media print { body{background:#fff}
   .sheet{max-width:none;margin:0;padding:0;box-shadow:none} }
 .top { display:flex; justify-content:space-between; gap:30px;
        border-bottom:2px solid #0F6E56; padding-bottom:18px }
 .mark { width:38px; height:38px; margin-bottom:8px }
 h1 { font-size:26px; margin:0 0 4px; letter-spacing:-.02em }
 .num { font:600 15px/1 ui-monospace,Menlo,monospace; color:#0F6E56 }
 .meta { text-align:right; font-size:12.5px; color:#57605c }
 .meta b { color:#1e2b26 }
 .parties { display:flex; gap:40px; margin:26px 0 }
 .parties h2 { font-size:11px; text-transform:uppercase;
   letter-spacing:.1em; color:#8b938e; margin:0 0 6px }
 table { width:100%; border-collapse:collapse; margin-top:10px }
 th { text-align:left; font-size:11px; text-transform:uppercase;
      letter-spacing:.08em; color:#8b938e; padding:8px 0;
      border-bottom:1px solid #d8ddda }
 td { padding:10px 0; border-bottom:1px solid #f1f4f2 }
 .r { text-align:right; font-variant-numeric:tabular-nums }
 .totals { margin-left:auto; width:260px; margin-top:14px }
 .totals div { display:flex; justify-content:space-between;
   padding:6px 0 }
 .totals .due { border-top:2px solid #0F6E56; margin-top:6px;
   padding-top:10px; font-size:17px; font-weight:700 }
 .pay { margin-top:30px; padding:16px; background:#f5f8f7;
   border:1px solid #e1e9e6; border-radius:8px; font-size:12.5px }
 .foot { margin-top:26px; color:#8b938e; font-size:11.5px }
 .paid { color:#0F6E56; font-weight:700 }
`;

const addressBlock = (b: Record<string, string>) => [
  b.legalName, b.address1,
  [b.city, b.region, b.postcode].filter(Boolean).join(', '),
  b.country,
  b.taxNumber ? 'Tax ' + b.taxNumber : '',
  b.email, b.phone,
].filter(Boolean).map(esc).join('<br>');

export const printInvoice = (d: {
  invoice: Record<string, string | number | null>;
  lines: Line[];
  business: Record<string, string>;
}): boolean => {
  const i = d.invoice;
  const b = d.business ?? {};
  const ccy = String(i.currency ?? 'USD');
  const total = Number(i.amount_cents ?? 0);

  // A subscription invoice with no explicit lines still has to show
  // what was charged, so the period becomes the line.
  const lines: Line[] = d.lines.length ? d.lines : [{
    description: 'Kestrelay subscription'
      + (i.period_start
        ? `, ${day(String(i.period_start))} to `
          + day(String(i.period_end)) : ''),
    quantity: 1,
    unit_cents: total,
    amount_cents: total,
  }];

  const rows = lines.map((l) =>
    `<tr><td>${esc(l.description)}</td>`
    + `<td class="r">${Number(l.quantity)}</td>`
    + `<td class="r">${money(l.unit_cents, ccy)}</td>`
    + `<td class="r">${money(l.amount_cents, ccy)}</td></tr>`).join('');

  const clientBlock = [
    i.legal_name || i.client_name,
    i.address, i.country,
    i.tax_number ? 'Tax ' + i.tax_number : '',
    i.billing_email,
  ].filter(Boolean).map(esc).join('<br>');

  const w = window.open('', '_blank', 'width=860,height=1000');
  if (!w) return false;

  w.document.write('<!doctype html><html><head><meta charset="utf-8">'
    + `<title>Invoice ${esc(i.number)}</title><style>${STYLE}`
    + '</style></head><body><div class="sheet">'
    + `<div class="top"><div>${MARK}<h1>Invoice</h1>`
    + `<span class="num">${esc(i.number)}</span></div>`
    + '<div class="meta">'
    + (b.legalName ? `<b>${esc(b.legalName)}</b><br>` : '')
    + addressBlock(b) + '</div></div>'
    + `<div class="parties"><div style="flex:1"><h2>Billed to</h2>`
    + `${clientBlock}</div><div style="flex:1"><h2>Details</h2>`
    + `Issued ${day(String(i.issued_at))}<br>`
    + (i.due_at ? `Due ${day(String(i.due_at))}<br>` : '')
    + (i.period_start
      ? `Period ${day(String(i.period_start))} to `
        + `${day(String(i.period_end))}<br>` : '')
    + 'Status ' + (i.paid_at
      ? `<span class="paid">Paid ${day(String(i.paid_at))}</span>`
      : esc(i.status))
    + '</div></div>'
    + '<table><thead><tr><th>Description</th><th class="r">Qty</th>'
    + '<th class="r">Unit</th><th class="r">Amount</th></tr></thead>'
    + `<tbody>${rows}</tbody></table>`
    + `<div class="totals"><div><span>Subtotal</span><span>`
    + `${money(total, ccy)}</span></div>`
    + `<div class="due"><span>${i.paid_at ? 'Paid' : 'Amount due'}`
    + `</span><span>${money(total, ccy)}</span></div></div>`
    + (b.payTo
      ? `<div class="pay"><b>How to pay</b><br>${esc(b.payTo)}</div>`
      : '')
    + `<p class="foot">Invoice ${esc(i.number)}`
    + (b.website ? `  |  ${esc(b.website)}` : '')
    + (b.email ? `  |  ${esc(b.email)}` : '')
    + '</p></div></body></html>');

  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 350);
  return true;
};
